/**
 * Erzeugt eine Google Maps zur Anzeige von Betrieben
 *
 * Features:
 * - Asynchrones Laden von Google Maps
 * - Integration Mapcluster + Fixes für Sonderfälle
 *
 * Erforderliche Parameter:
 * @param target {String}: ID des Karten-Elements
 * @param onClick {Function}: Callback für Klicks auf Marker
 *
 * Optionale Parameter
 * @param center {Objekt, null}: Mitte der Karte (Standard: Oberstdorf)
 * @param zoom {Number, null}: Zoomlevel (Standard: 15)
 *
 * Referenz:
 * https://googlemaps.github.io/js-marker-clusterer/docs/reference.html
 */

var $ = require('jquery');
var _ = require('underscore');
var Promise = require('promise-polyfill');


const defaultCoordinates = {
    // Oberstdorf
    100: {
        lat: 47.40925187377930000,
        lng: 10.27934982299804700
    },

    // Ammersee-Lech
    620: {
        lat: 48.0458172,
        lng: 10.8659336
    },

    // Wertach
    9350: {
        lat: 47.602137,
        lng: 10.411246,
    },
}

/**
 * Returns the default coordinates for the current client, destination,
 * or Oberstdorf if unknown.
 */
function getDefaultCoordinates() {
    const client = parseInt(window.Tramino.client, 10);
    const destination = parseInt(window.Tramino.destination, 10);

    return ( defaultCoordinates[client]
        || defaultCoordinates[destination]
        || defaultCoordinates[100]
    );
}

var TraminoClientsMap = function(options) {
    _.bindAll(this, "init", "loadMaps", "addMarkers", "clearMarkers");

    this.options = options;
    this.loadingPromise = null;
    this.initialized = false;

    console.log(this.options);


    if ( !this.options.center ) {
        this.options.center = getDefaultCoordinates();
    }
    if ( !this.options.zoom ) {
        this.options.zoom = 15;
    }
    if ( !this.options.autofit ) {
        this.options.autofit = false;
    }
    if ( !this.options.api_key ) {
        this.options.api_key = "AIzaSyDprYveDeS9YkRYuRNEG3i_hrfUa140JB4";
    }

    this.loadMaps();
}

/**
 * Google Maps asyncron laden, falls notwendig.
 *
 * @return {Promise} Feuert wenn Google Maps bereit ist. Das gelieferte Ergebnis ist undefiniert.
 */
TraminoClientsMap.prototype.loadMaps = function() {
    if ( window.google && google.maps ) {
        console.log("[TraminoClientsMap] loadMaps: Google Maps schon geladen");
        return Promise.resolve(true);
    }

    if ( this.loadingPromise === null ) {
        var maps_url = "https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry&callback=TraminoClientsMapReady&key=" + this.options.api_key;
        var cluster_url = "/tramino/js/markerclusterer/markerclusterer_compiled.js";

        console.log("[TraminoClientsMap] loadMaps: Google Maps wird geladen");
        this.loadingPromise = new Promise(function(resolve, reject) {
            window.TraminoClientsMapReady = function() {
                console.log("[TraminoClientsMap] loadMaps: Google Maps ist bereit");
                resolve();
                delete window.TraminoClientsMapReady;
            }

            Promise.all([
                Promise.resolve($.getScript(maps_url)),
                Promise.resolve($.getScript(cluster_url)),
            ]).then(function() {
                console.log("[TraminoClientsMap] loadMaps: Google Maps-Skripte wurden geladen");
            });
        });
    }
    return this.loadingPromise;
};

/**
 * Karte initialisieren.
 *
 * @return {Promise}
 */
TraminoClientsMap.prototype.init = function() {
    return this.loadMaps().then(function() {
        console.log("[TraminoClientsMap] init");
        this.map = new google.maps.Map(document.getElementById(this.options.target), {
            zoom: this.options.zoom,
            center: this.options.center,
            scrollwheel: false,
        });

        if ( typeof window.MarkerClusterer !== "undefined" ) {
            this.mc = new MarkerClusterer(this.map, [], {
                gridSize: 25,
                maxZoom: 15,
            });
        }

        this.infoWindow = new google.maps.InfoWindow({
            content: "Kein Inhalt gefunden",
        });
        this.initialized = true;

        if ( this.options.data ) {
            this.addMarkers(this.options.data);
        }
    }.bind(this));
};

TraminoClientsMap.prototype.displayInfo = function(marker, content, options) {
    if ( !this.initialized ) return;

    if ( options ) {
        this.infoWindow.setOptions(options);
    }
    this.infoWindow.setContent(content);
    this.infoWindow.open(this.map, marker);
};

TraminoClientsMap.prototype.addMarkers = function(clients) {
    if ( !this.initialized ) return;

    var self = this;
    var markers = [];

    var addMarker = function(betrieb) {
        var marker = new google.maps.Marker({
            position: {
                lat: parseFloat(betrieb.lat),
                lng: parseFloat(betrieb.lng)
            },
            /*icon: '',*/
            title: betrieb.name,
        });
        marker.addListener('click', function(e) {
            self.options.onClick(e, marker, betrieb);
        });
        markers.push(marker);
    }

    for ( var i = 0; i < clients.length; ++i ) {
        var betrieb = clients[i];
        addMarker(betrieb);
    }

    this.mc.addMarkers(markers);

    if ( this.options.autofit ) {
        /* Karte neu ausrichten */
        var bounds = new google.maps.LatLngBounds();
        _.each(clients, function(b) {
            if ( b.lat && b.lng ) {
                bounds.extend(new google.maps.LatLng(b.lat, b.lng));
            }
        });

        /* Fake-Marker falls keine Marker gesetzt */
        if ( clients.length === 0 ) {
            let coords = getDefaultCoordinates();
            bounds.extend(new google.maps.LatLng(coords.lat, coords.lng));
        }

        /* Verhindern dass wir zu nah zoomen */
        if ( bounds.getNorthEast().equals(bounds.getSouthWest()) ) {
           var extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.01, bounds.getNorthEast().lng() + 0.01);
           var extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.01, bounds.getNorthEast().lng() - 0.01);
           bounds.extend(extendPoint1);
           bounds.extend(extendPoint2);
        }

        this.map.setCenter(bounds.getCenter());
        this.map.fitBounds(bounds);
    }
};


TraminoClientsMap.prototype.clearMarkers = function() {
    if ( !this.initialized ) return;

    this.mc.clearMarkers();
};

module.exports = TraminoClientsMap;



// WEBPACK FOOTER //
// /Users/bm/app/code/jsapps/modules/reserva/TraminoClientsMap.js